import {
  Card,
  CardContent,
  Checkbox,
  CheckboxGroup,
  cn,
  Field,
  FieldError,
  Label,
} from "@beweegbaas/ui";
import { ClientOnly, createFileRoute, stripSearchParams, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { motion } from "motion/react";
import { z } from "zod";
import { HistoryBackLink } from "#/components/back-link";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { RapportPdfFallback } from "#/modules/rapporten/pdf-components/documents/pdf.rapport.fallback";
import { ReportsPdfPreviewLoader } from "./-reports-pdf-preview-loader";
import { ReportsStep4DownloadButton } from "./-reports-step4-download-button";
import {
  getRapportStep4Data,
  submitUpdateRapportElementen,
} from "#/modules/rapporten/rapporten.functions";
import { RapportInstellingenDialog } from "#/modules/rapporten/components/card.rapport-preferences";
import { RapportenFlowSummary } from "#/modules/rapporten/rapporten-flow-summary";
import { step4Schema } from "#/modules/rapporten/rapporten-flow.steps";
import { useRapportPdf } from "#/modules/rapporten/use-rapport-pdf";
import { useRapportenFlow } from "#/modules/rapporten/use-rapporten-flow";
import {
  getCombinedThemaDomeinLeerlijnTranslation,
  getRapportElementenTranslation,
} from "#/modules/util.i18n/enum-mapping";
import { m } from "#/paraglide/messages";
import { TreeFilter } from "#/modules/util.tree-filter-field/components/field.tree-filter";
import { RapportElementen } from "#/schema.nswag";
import { useAppForm } from "#/modules/util.forms/form";
import { isApiEnumValue } from "#/modules/util.helpers/zod-enum";

const isRapportElement = isApiEnumValue(RapportElementen);
export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/reports/flow/$id/step/4/",
)({
  // PDF-viewer state — URL-backed but client-only (deliberately NOT in loaderDeps).
  validateSearch: z.object({
    page: z.number().int().catch(1).default(1),
    studentId: z.string().optional(),
  }),
  // Keeps the default page out of the URL.
  search: { middlewares: [stripSearchParams({ page: 1 })] },
  loader: ({ params }) => getRapportStep4Data({ data: { rapportId: params.id } }),
  component: ReportsStep4Page,
  errorComponent: ReportsStep4Error,
});

function ReportsStep4Page() {
  const { rapport } = useRapportenFlow();
  const { rapportLeerlingResultaten } = Route.useLoaderData();
  const submitUpdateElementen = useServerFn(submitUpdateRapportElementen);
  const { selectedStudentId, selectedStudent } = useRapportPdf();
  const params = Route.useParams();
  const router = useRouter();
  const { page: currentPage } = Route.useSearch();
  const navigate = Route.useNavigate();
  const setCurrentPage = (value: number) =>
    navigate({ search: (prev) => ({ ...prev, page: value }), resetScroll: false });

  const form = useAppForm({
    defaultValues: {
      rapportElementen: rapport?.rapportElementen ? rapport.rapportElementen : [],
      leerlijnFilter: rapportLeerlingResultaten.filter.leerlijnFilter.selectedFilterIds,
      sociaalEmotioneelDomeinFilter:
        rapportLeerlingResultaten.filter.sociaalEmotioneelDomeinFilter.selectedFilterIds,
    },
    validators: {
      onChange: step4Schema,
    },
    onSubmitMeta: {
      submitAction: "update",
    },
    onSubmit: async ({ value }) => {
      try {
        await submitUpdateElementen({
          data: {
            rapportId: params.id,
            rapportElementen: value.rapportElementen,
            leerlijnFilter: value.leerlijnFilter,
            sociaalEmotioneelDomeinFilter: value.sociaalEmotioneelDomeinFilter,
          },
        });
      } catch {
        // toastErrorMiddleware already surfaced the error
        return;
      }
      await router.invalidate();
    },
  });

  const hasLeerlijnen = rapportLeerlingResultaten.leerlingen.some(
    (leerling) => leerling.leerlijnAverages.length > 0,
  );
  const hasSociaalEmotioneelThemas = rapportLeerlingResultaten.leerlingen.some(
    (leerling) => leerling.sociaalEmotioneelThemaDomeinAverages.length > 0,
  );

  const frontPageRapportElementen = [
    RapportElementen.Avatar,
    RapportElementen.IkCirkelBeweegactiviteiten,
    RapportElementen.IkCirkelSociaalEmotioneelThemas,
    RapportElementen.RapportNotitie,
    RapportElementen.TopResultaten,
    RapportElementen.SportdeelnameVoorkant,
    RapportElementen.ZwemvaardigheidVoorkant,
    RapportElementen.FietsvaardigheidVoorkant,
  ];

  const backPageRapportElementen = [
    RapportElementen.Beweegresultaten,
    RapportElementen.SociaalEmotioneelThemaResultaten,
    RapportElementen.SportdeelnameAchterkant,
    RapportElementen.ZwemvaardigheidAchterkant,
    RapportElementen.FietsvaardigheidAchterkant,
  ];

  return (
    <Page>
      <Page.Header>
        <HistoryBackLink
          className={cn("max-md:self-start max-sm:mb-4")}
          fallback={{ from: Route.fullPath, to: "../3" }}
        />
        <Page.Title>{m["rapporten.config.title"]()}</Page.Title>

        <Page.Header.Actions>
          <Page.Header.SecondaryActions>
            <RapportInstellingenDialog />
          </Page.Header.SecondaryActions>
        </Page.Header.Actions>
      </Page.Header>
      <Page.Container>
        <Page.Body>
          <Page.SidebarLayout>
            <div className="flex grow flex-col gap-4 sm:flex-row">
              <Card className="flex flex-1 grow flex-col gap-6">
                <CardContent className="flex flex-1 flex-col gap-6">
                  <form
                    className="flex flex-col gap-6"
                    onSubmit={(e) => {
                      e.preventDefault();
                    }}
                  >
                    <form.Field
                      name="rapportElementen"
                      children={(field) => {
                        const isInvalid = !field.state.meta.isValid && field.state.meta.isTouched;
                        const errorMessage =
                          isInvalid && field.state.meta.errors[0]?.message
                            ? field.state.meta.errors[0].message
                            : undefined;

                        return (
                          <Field data-invalid={isInvalid}>
                            <div className="flex flex-col gap-6">
                              <CheckboxGroup
                                aria-label={m["rapporten.rapport_elementen"]()}
                                value={field.state.value}
                                onValueChange={(value) => {
                                  field.handleChange(value.filter(isRapportElement));
                                  void setCurrentPage(1);
                                  void form.handleSubmit({ submitAction: "update" });
                                }}
                              >
                                <strong className="text-muted-foreground text-xs uppercase">
                                  Voorkant
                                </strong>
                                {frontPageRapportElementen.map((element) => {
                                  const isDisabled =
                                    (element === RapportElementen.IkCirkelBeweegactiviteiten &&
                                      !hasLeerlijnen) ||
                                    (element === RapportElementen.IkCirkelSociaalEmotioneelThemas &&
                                      !hasSociaalEmotioneelThemas);
                                  return (
                                    <div
                                      key={element}
                                      className="flex cursor-pointer items-center gap-2"
                                    >
                                      <Checkbox
                                        id={element}
                                        value={element}
                                        disabled={isDisabled}
                                      />
                                      <Label htmlFor={element}>
                                        {getRapportElementenTranslation(element)}
                                      </Label>
                                    </div>
                                  );
                                })}
                              </CheckboxGroup>

                              <hr />

                              <CheckboxGroup
                                aria-label={m["rapporten.rapport_elementen"]()}
                                value={field.state.value}
                                onValueChange={(value) => {
                                  field.handleChange(value.filter(isRapportElement));
                                  if (currentPage === 1) {
                                    void setCurrentPage(2);
                                  }
                                  void form.handleSubmit({ submitAction: "update" });
                                }}
                              >
                                <strong className="text-muted-foreground text-xs uppercase">
                                  Achterkant
                                </strong>
                                {backPageRapportElementen.map((element) => (
                                  <label
                                    key={element}
                                    className="flex cursor-pointer items-center gap-2"
                                  >
                                    <Checkbox value={element} />
                                    <span>{getRapportElementenTranslation(element)}</span>
                                  </label>
                                ))}
                              </CheckboxGroup>

                              <hr />

                              <CheckboxGroup
                                aria-label="Informatieblad"
                                value={field.state.value}
                                onValueChange={(value) => {
                                  field.handleChange(value.filter(isRapportElement));
                                  if (!value.includes(RapportElementen.Handout)) {
                                    void setCurrentPage(1);
                                  }
                                  void form.handleSubmit({ submitAction: "update" });
                                }}
                              >
                                <strong className="text-muted-foreground text-xs uppercase">
                                  Extra pagina's
                                </strong>
                                <label className="flex cursor-pointer items-center gap-2">
                                  <Checkbox value={RapportElementen.Handout} />
                                  <span>
                                    {getRapportElementenTranslation(RapportElementen.Handout)}
                                  </span>
                                </label>
                              </CheckboxGroup>

                              <hr />
                            </div>
                            {errorMessage && <FieldError>{errorMessage}</FieldError>}
                          </Field>
                        );
                      }}
                    />

                    <form.Field
                      name="leerlijnFilter"
                      children={(field) => {
                        const hasItems =
                          rapportLeerlingResultaten.filter.leerlijnFilter.items.length > 0;
                        return (
                          <div className="flex flex-col gap-2">
                            <strong className="text-muted-foreground text-xs uppercase">
                              Leerlijnen
                            </strong>
                            {hasItems ? (
                              <TreeFilter
                                treeFilterModels={
                                  rapportLeerlingResultaten.filter.leerlijnFilter.items
                                }
                                onSelectionChange={(value) => {
                                  field.handleChange(value);
                                  void form.handleSubmit({ submitAction: "update" });
                                }}
                                selectedKeys={field.state.value}
                                renderItemContent={(item) => (
                                  <span>
                                    {getCombinedThemaDomeinLeerlijnTranslation(item.name)}
                                  </span>
                                )}
                              />
                            ) : (
                              <p className="text-muted-foreground text-sm">
                                Geen leerlijnen gevonden
                              </p>
                            )}
                          </div>
                        );
                      }}
                    />

                    <form.Field
                      name="sociaalEmotioneelDomeinFilter"
                      children={(field) => {
                        const hasItems =
                          rapportLeerlingResultaten.filter.sociaalEmotioneelDomeinFilter.items
                            .length > 0;
                        return (
                          <div className="flex flex-col gap-2">
                            <strong className="text-muted-foreground text-xs uppercase">
                              Sociaal-emotionele domeinen
                            </strong>
                            {hasItems ? (
                              <TreeFilter
                                treeFilterModels={
                                  rapportLeerlingResultaten.filter.sociaalEmotioneelDomeinFilter
                                    .items
                                }
                                onSelectionChange={(value) => {
                                  field.handleChange(value);
                                  void form.handleSubmit({ submitAction: "update" });
                                }}
                                selectedKeys={field.state.value}
                                renderItemContent={(item) => (
                                  <span>
                                    {getCombinedThemaDomeinLeerlijnTranslation(item.name)}
                                  </span>
                                )}
                              />
                            ) : (
                              <p className="text-muted-foreground text-sm">
                                Geen sociaal-emotionele domeinen gevonden
                              </p>
                            )}
                          </div>
                        );
                      }}
                    />
                  </form>

                  <motion.hr
                    transition={{ duration: 0.1 }}
                    layout={"position"}
                    className="mt-auto"
                  />

                  <ReportsStep4DownloadButton
                    rapport={rapport}
                    rapportLeerlingResultaten={rapportLeerlingResultaten}
                    locale={params.locale}
                    school={params.school}
                  />
                </CardContent>
              </Card>
              <div className="flex flex-1 flex-col p-0">
                <div className="bg-muted rounded p-4 shadow">
                  <ClientOnly fallback={<RapportPdfFallback />}>
                    <ReportsPdfPreviewLoader
                      key={selectedStudentId}
                      LeerlingRapportModel={
                        selectedStudent || rapportLeerlingResultaten.leerlingen[0]
                      }
                    />
                  </ClientOnly>
                </div>
              </div>
            </div>

            <Page.Sidebar className="max-lg:hidden">
              <RapportenFlowSummary />
            </Page.Sidebar>
          </Page.SidebarLayout>
        </Page.Body>
      </Page.Container>
    </Page>
  );
}

function ReportsStep4Error({ error }: { error: unknown }) {
  const routeParams = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={routeParams} />;
}
